import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { paths } from '../app/router';
import { api } from '../sync/api';
import type { Role } from '../sync/boardsApi';
import { IconTrash } from '../ui/icons';
import { PanelFrame } from './floating';
import { useBoard } from './context';

interface ShareLink {
  token: string;
  role: 'editor' | 'viewer';
  hasPassword: boolean;
  expiresAt: string | null;
  createdAt: string;
}
interface Member {
  id: string;
  email: string;
  name: string;
  role: Role;
}

/** Owner's sharing panel: share links (role, optional password and expiry) and members by email. */
export function SharePanel({ onClose }: { onClose: () => void }) {
  const board = useBoard();
  const [links, setLinks] = useState<ShareLink[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [role, setRole] = useState<'viewer' | 'editor'>('viewer');
  const [password, setPassword] = useState('');
  const [expires, setExpires] = useState('');
  const [copied, setCopied] = useState<string | null>(null);
  const [memberEmail, setMemberEmail] = useState('');
  const [memberRole, setMemberRole] = useState<'viewer' | 'editor'>('editor');

  const refresh = useCallback(async () => {
    try {
      const r = await api.get<{ links: ShareLink[]; members: Member[] }>(`/boards/${board.boardId}/share`);
      setLinks(r.links);
      setMembers(r.members);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [board.boardId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const linkUrl = (token: string) => `${location.origin}${location.pathname}${paths.share(token)}`;

  const copy = async (token: string) => {
    try {
      await navigator.clipboard.writeText(linkUrl(token));
      setCopied(token);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      prompt('Share link', linkUrl(token));
    }
  };

  const createLink = async (e: FormEvent) => {
    e.preventDefault();
    try {
      const r = await api.post<{ link: ShareLink }>(`/boards/${board.boardId}/share`, {
        role,
        password: password || undefined,
        expiresDays: expires ? Number(expires) : undefined,
      });
      setPassword('');
      await refresh();
      void copy(r.link.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const removeLink = async (token: string) => {
    await api.delete(`/boards/${board.boardId}/share/${token}`);
    void refresh();
  };

  const addMember = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post(`/boards/${board.boardId}/members`, { email: memberEmail, role: memberRole });
      setMemberEmail('');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  const removeMember = async (id: string) => {
    await api.delete(`/boards/${board.boardId}/members/${id}`);
    void refresh();
  };

  return (
    <PanelFrame kind="share" title="Share" className="share-panel" onClose={onClose}>
      <div className="insp-body">
        {error && <div className="form-error">{error}</div>}

        <div className="insp-label">New link</div>
        <form className="share-form" onSubmit={createLink}>
          <div className="btn-row">
            {(['viewer', 'editor'] as const).map((r) => (
              <button key={r} type="button" className={`btn${role === r ? ' is-active' : ''}`} onClick={() => setRole(r)}>
                {r === 'viewer' ? 'View only' : 'Can edit'}
              </button>
            ))}
          </div>
          <input className="share-input" type="text" placeholder="Password (optional)" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="off" />
          <select className="font-select" value={expires} onChange={(e) => setExpires(e.target.value)}>
            <option value="">Never expires</option>
            <option value="1">Expires in 1 day</option>
            <option value="7">Expires in 7 days</option>
            <option value="30">Expires in 30 days</option>
          </select>
          <button type="submit" className="btn btn-primary">
            Create link & copy
          </button>
        </form>

        {links.length > 0 && (
          <>
            <div className="insp-label">Links</div>
            <ul className="share-list">
              {links.map((l) => (
                <li key={l.token}>
                  <span className="share-role">{l.role === 'viewer' ? 'view' : 'edit'}</span>
                  <span className="share-meta">
                    {l.hasPassword ? 'password' : 'open'}
                    {l.expiresAt ? ` · until ${l.expiresAt.slice(0, 10)}` : ''}
                  </span>
                  <button type="button" className="btn" onClick={() => copy(l.token)}>
                    {copied === l.token ? 'Copied' : 'Copy'}
                  </button>
                  <button type="button" className="btn danger" title="Revoke" onClick={() => removeLink(l.token)}>
                    <IconTrash />
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}

        <div className="insp-label">Members (accounts on this server)</div>
        <form className="share-form" onSubmit={addMember}>
          <input className="share-input" type="email" placeholder="email@example.com" required value={memberEmail} onChange={(e) => setMemberEmail(e.target.value)} />
          <div className="btn-row">
            <select className="font-select" value={memberRole} onChange={(e) => setMemberRole(e.target.value as 'viewer' | 'editor')}>
              <option value="editor">Can edit</option>
              <option value="viewer">View only</option>
            </select>
            <button type="submit" className="btn">
              Add
            </button>
          </div>
        </form>
        {members.length > 0 && (
          <ul className="share-list">
            {members.map((m) => (
              <li key={m.id}>
                <span className="share-role">{m.role === 'viewer' ? 'view' : 'edit'}</span>
                <span className="share-meta" title={m.email}>
                  {m.name}
                </span>
                <button type="button" className="btn danger" title="Remove" onClick={() => removeMember(m.id)}>
                  <IconTrash />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="insp-muted">People without an account need an invite link from the dashboard first.</div>
      </div>
    </PanelFrame>
  );
}
